import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { AsyncPipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import moment, { type Moment } from 'moment';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { AdvizeoTooltipDirective } from '../../../directives/advizeo-tooltip/advizeo-tooltip.directive';
import type { BuildingId, Period } from '../../../interfaces/Building.interface';
import type { TileConfig, TileData } from '../../../interfaces/Tile.interface';
import type { HomeTilesSettings, HomeTilesTranslationKeys } from '../../../interfaces/Widget.interface';
import { VatModes } from '../../../providers/budgets/budgets.constants';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { ProjectsProvider } from '../../../providers/projects/projects.provider';
import { TilesProvider } from '../../../providers/tiles/tiles.provider';
import { WidgetSize } from '../../../providers/widgets.contants';
import { Page, RoutingService } from '../../../services/routing/routing.service';
import { HelpButtonComponent } from '../../help-button/help-button';
import { TileSpawnerComponent } from '../../tile/tile-spawner/tile-spawner';
import { AbstractWidgetClass } from '../AbstractWidget.class';

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./widget-home-tiles.ng-cli.scss'],
    selector: 'widget-home-tiles',
    templateUrl: 'widget-home-tiles.html',
    imports: [
        HelpButtonComponent,
        UiIconButtonComponent,
        AdvizeoTooltipDirective,
        TileSpawnerComponent,
        AsyncPipe,
        LowerCasePipe,
        TranslatePipe
    ]
})
export class WidgetHomeTilesComponent extends AbstractWidgetClass<HomeTilesTranslationKeys, HomeTilesSettings> {
    private _routingService = inject(RoutingService);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _tilesProvider = inject(TilesProvider);
    private _projectsProvider = inject(ProjectsProvider);

    size = WidgetSize.FULL;

    private _buildingUid!: BuildingId;
    private _vatMode!: VatModes;
    tiles: TileData[] = [];
    dateStart!: Moment;
    dateEnd!: Moment;
    period: Period | null = null;
    helpButtonUrl = 'gestion-des-tuiles-personnalisees'; // Nouvelle URL A METTRE

    tilesConfig: TileConfig = {};

    ngOnInit() {
        combineLatest(this._dashboardingProvider.getMeaningfulBuildingId$(), this._projectsProvider.getVatMode$())
            .pipe(
                filter(([, vatMode]) => !!vatMode),
                untilDestroyed(this)
            )
            .subscribe(([buildingId, vatMode]) => {
                this._vatMode = vatMode!;
                this.initWidget(buildingId);
            });
    }

    goToNewTile() {
        this._routingService.goTo(Page.CUSTOM_TILE_CONFIGURATION, { tileGroup: this.config.zone });
    }

    private initWidget(buildingId: BuildingId) {
        if (this._buildingUid === buildingId) return;
        this._buildingUid = buildingId;

        this.tilesConfig.hasTileAdminAccess$ = this._tilesProvider.hasTileAdminOption$(this._buildingUid);

        if (this.config && this.config.ignorePeriodsStore) {
            this.dateStart = moment().subtract(1, 'years');
            this.dateEnd = moment();
            this.fetchTiles();
        } else {
            this._dashboardingProvider
                .getSelectedPeriod$()
                .pipe(
                    distinctUntilChanged((oldPeriod, newPeriod) => {
                        // Comparison function -> returns false if different objects
                        if (!oldPeriod && !newPeriod) return true;
                        if (!oldPeriod || !newPeriod) return false;
                        return oldPeriod.start === newPeriod.start && oldPeriod.end === newPeriod.end;
                    }),
                    untilDestroyed(this)
                )
                .subscribe(period => {
                    this.period = period;
                    if (period) {
                        this.dateStart = moment(period.start);
                        this.dateEnd = moment(period.end);
                        this.fetchTiles();
                    } else {
                        this.dateStart = null!;
                        this.dateEnd = null!;
                    }
                });
        }
    }

    private fetchTiles() {
        if (this.config.zone && this.dateStart && this.dateEnd && this._buildingUid) {
            this._tilesProvider
                .fetchBuildingTilesByGroup(
                    this._buildingUid,
                    this.config.zone,
                    this.dateStart,
                    this.dateEnd,
                    this._vatMode === VatModes.VAT_INCL
                )
                .then((tilesData: TileData[]) => {
                    this.tiles = tilesData.sort((a, b) => a.name.localeCompare(b.name)).sort((a, b) => (a.tileType < b.tileType ? -1 : 1));
                })
                .catch(err => console.log(err));
        }
    }

    deleteTile(tileIndex: number) {
        this.tiles.splice(tileIndex, 1);
    }
}
